import { Suspense, lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { P } from "./lib/permissions";
import { useSession } from "./app/session";
import {
  FullPageWait,
  NotFound,
  RequireAuth,
  RequireInternal,
  RequirePermission,
} from "./app/guards";
import { Shell } from "./components/layout/Shell";
import { LoginScreen } from "./screens/auth/LoginScreen";

/*
 * Screens are loaded on demand.
 *
 * Every route in one screen module shares a chunk, so opening the sponsor
 * dashboard fetches the sponsor screens and nothing else. The alternative —
 * one bundle for all 93 routes — makes the first paint pay for every screen
 * the visitor will never open.
 */
const loadAccountScreen = () => import("./screens/account/AccountScreen");
const AccountScreen = lazy(() => loadAccountScreen().then((m) => ({ default: m.AccountScreen })));
const loadAdministrationScreens = () => import("./screens/administration/AdministrationScreens");
const FeatureFlagsScreen = lazy(() => loadAdministrationScreens().then((m) => ({ default: m.FeatureFlagsScreen })));
const GroupsScreen = lazy(() => loadAdministrationScreens().then((m) => ({ default: m.GroupsScreen })));
const RolesMatrixScreen = lazy(() => loadAdministrationScreens().then((m) => ({ default: m.RolesMatrixScreen })));
const SecurityAuditScreen = lazy(() => loadAdministrationScreens().then((m) => ({ default: m.SecurityAuditScreen })));
const TenantBrandingScreen = lazy(() => loadAdministrationScreens().then((m) => ({ default: m.TenantBrandingScreen })));
const UsersAccessScreen = lazy(() => loadAdministrationScreens().then((m) => ({ default: m.UsersAccessScreen })));
const loadSaasScreens = () => import("./screens/administration/SaasScreens");
const SaasTenantScreen = lazy(() => loadSaasScreens().then((m) => ({ default: m.SaasTenantScreen })));
const SubscriptionPlansScreen = lazy(() => loadSaasScreens().then((m) => ({ default: m.SubscriptionPlansScreen })));
const loadAnalyticsScreens = () => import("./screens/analytics/AnalyticsScreens");
const AnalyticsCommandScreen = lazy(() => loadAnalyticsScreens().then((m) => ({ default: m.AnalyticsCommandScreen })));
const ReportBuilderScreen = lazy(() => loadAnalyticsScreens().then((m) => ({ default: m.ReportBuilderScreen })));
const loadAlertsScreen = () => import("./screens/command/AlertsScreen");
const AlertsScreen = lazy(() => loadAlertsScreen().then((m) => ({ default: m.AlertsScreen })));
const loadApprovalsScreen = () => import("./screens/command/ApprovalsScreen");
const ApprovalsScreen = lazy(() => loadApprovalsScreen().then((m) => ({ default: m.ApprovalsScreen })));
const loadExecutiveCommandScreen = () => import("./screens/command/ExecutiveCommandScreen");
const ExecutiveCommandScreen = lazy(() => loadExecutiveCommandScreen().then((m) => ({ default: m.ExecutiveCommandScreen })));
const loadGlobalOverviewScreen = () => import("./screens/command/GlobalOverviewScreen");
const GlobalOverviewScreen = lazy(() => loadGlobalOverviewScreen().then((m) => ({ default: m.GlobalOverviewScreen })));
const loadIncidentCommandScreen = () => import("./screens/command/IncidentCommandScreen");
const IncidentCommandScreen = lazy(() => loadIncidentCommandScreen().then((m) => ({ default: m.IncidentCommandScreen })));
const loadLiveOperationsScreen = () => import("./screens/command/LiveOperationsScreen");
const LiveOperationsScreen = lazy(() => loadLiveOperationsScreen().then((m) => ({ default: m.LiveOperationsScreen })));
const loadMyWorkScreen = () => import("./screens/command/MyWorkScreen");
const MyWorkScreen = lazy(() => loadMyWorkScreen().then((m) => ({ default: m.MyWorkScreen })));
const loadRiskRegisterScreen = () => import("./screens/command/RiskRegisterScreen");
const RiskRegisterScreen = lazy(() => loadRiskRegisterScreen().then((m) => ({ default: m.RiskRegisterScreen })));
const loadSponsorScreens = () => import("./screens/commercial/SponsorScreens");
const ExhibitorMarketplaceScreen = lazy(() => loadSponsorScreens().then((m) => ({ default: m.ExhibitorMarketplaceScreen })));
const SponsorCommandScreen = lazy(() => loadSponsorScreens().then((m) => ({ default: m.SponsorCommandScreen })));
const SponsorDetailScreen = lazy(() => loadSponsorScreens().then((m) => ({ default: m.SponsorDetailScreen })));
const SponsorshipBuilderScreen = lazy(() => loadSponsorScreens().then((m) => ({ default: m.SponsorshipBuilderScreen })));
const loadCommunicationScreens = () => import("./screens/communications/CommunicationScreens");
const AudienceBuilderScreen = lazy(() => loadCommunicationScreens().then((m) => ({ default: m.AudienceBuilderScreen })));
const CampaignBuilderScreen = lazy(() => loadCommunicationScreens().then((m) => ({ default: m.CampaignBuilderScreen })));
const CommunicationsCommandScreen = lazy(() => loadCommunicationScreens().then((m) => ({ default: m.CommunicationsCommandScreen })));
const EmailStudioScreen = lazy(() => loadCommunicationScreens().then((m) => ({ default: m.EmailStudioScreen })));
const SenderProfilesScreen = lazy(() => loadCommunicationScreens().then((m) => ({ default: m.SenderProfilesScreen })));
const WhatsAppStudioScreen = lazy(() => loadCommunicationScreens().then((m) => ({ default: m.WhatsAppStudioScreen })));
const loadCrmScreens = () => import("./screens/crm/CrmScreens");
const ClientProfileScreen = lazy(() => loadCrmScreens().then((m) => ({ default: m.ClientProfileScreen })));
const ClientsScreen = lazy(() => loadCrmScreens().then((m) => ({ default: m.ClientsScreen })));
const OpportunityDetailScreen = lazy(() => loadCrmScreens().then((m) => ({ default: m.OpportunityDetailScreen })));
const PeopleScreen = lazy(() => loadCrmScreens().then((m) => ({ default: m.PeopleScreen })));
const PersonProfileScreen = lazy(() => loadCrmScreens().then((m) => ({ default: m.PersonProfileScreen })));
const SalesPipelineScreen = lazy(() => loadCrmScreens().then((m) => ({ default: m.SalesPipelineScreen })));
const loadCreativeProductionScreens = () => import("./screens/delivery/CreativeProductionScreens");
const CreativeDetailScreen = lazy(() => loadCreativeProductionScreens().then((m) => ({ default: m.CreativeDetailScreen })));
const CreativeStudioScreen = lazy(() => loadCreativeProductionScreens().then((m) => ({ default: m.CreativeStudioScreen })));
const ProductionCommandScreen = lazy(() => loadCreativeProductionScreens().then((m) => ({ default: m.ProductionCommandScreen })));
const ProductionItemScreen = lazy(() => loadCreativeProductionScreens().then((m) => ({ default: m.ProductionItemScreen })));
const VendorRfqScreen = lazy(() => loadCreativeProductionScreens().then((m) => ({ default: m.VendorRfqScreen })));
const loadProjectScreens = () => import("./screens/delivery/ProjectScreens");
const ProjectWorkspaceScreen = lazy(() => loadProjectScreens().then((m) => ({ default: m.ProjectWorkspaceScreen })));
const ProjectsScreen = lazy(() => loadProjectScreens().then((m) => ({ default: m.ProjectsScreen })));
const StaffDeploymentScreen = lazy(() => loadProjectScreens().then((m) => ({ default: m.StaffDeploymentScreen })));
const loadDocumentsScreen = () => import("./screens/documents/DocumentsScreen");
const DocumentsScreen = lazy(() => loadDocumentsScreen().then((m) => ({ default: m.DocumentsScreen })));
const loadEventCommandCenterScreen = () => import("./screens/events/EventCommandCenterScreen");
const EventCommandCenterScreen = lazy(() => loadEventCommandCenterScreen().then((m) => ({ default: m.EventCommandCenterScreen })));
const loadEventSettingsScreen = () => import("./screens/events/EventSettingsScreen");
const EventSettingsScreen = lazy(() => loadEventSettingsScreen().then((m) => ({ default: m.EventSettingsScreen })));
const loadEventWorkspace = () => import("./screens/events/EventWorkspace");
const EventWorkspace = lazy(() => loadEventWorkspace().then((m) => ({ default: m.EventWorkspace })));
const loadVenuesScreen = () => import("./screens/events/VenuesScreen");
const VenuesScreen = lazy(() => loadVenuesScreen().then((m) => ({ default: m.VenuesScreen })));
const loadEventsPortfolioScreen = () => import("./screens/events/EventsPortfolioScreen");
const EventsPortfolioScreen = lazy(() => loadEventsPortfolioScreen().then((m) => ({ default: m.EventsPortfolioScreen })));
const loadFinanceScreens = () => import("./screens/finance/FinanceScreens");
const EventBudgetScreen = lazy(() => loadFinanceScreens().then((m) => ({ default: m.EventBudgetScreen })));
const FinanceCommandScreen = lazy(() => loadFinanceScreens().then((m) => ({ default: m.FinanceCommandScreen })));
const InvoiceDetailScreen = lazy(() => loadFinanceScreens().then((m) => ({ default: m.InvoiceDetailScreen })));
const loadExchangeRateScreens = () => import("./screens/finance/ExchangeRateScreens");
const ExchangeRatesScreen = lazy(() => loadExchangeRateScreens().then((m) => ({ default: m.ExchangeRatesScreen })));
const loadAiCommandCenterScreen = () => import("./screens/intelligence/AiCommandCenterScreen");
const AiCommandCenterScreen = lazy(() => loadAiCommandCenterScreen().then((m) => ({ default: m.AiCommandCenterScreen })));
const loadOnsiteScreens = () => import("./screens/onsite/OnsiteScreens");
const CheckInScreen = lazy(() => loadOnsiteScreens().then((m) => ({ default: m.CheckInScreen })));
const OnsiteCommandScreen = lazy(() => loadOnsiteScreens().then((m) => ({ default: m.OnsiteCommandScreen })));
const loadAutomationScreens = () => import("./screens/platform/AutomationScreens");
const AutomationBuilderScreen = lazy(() => loadAutomationScreens().then((m) => ({ default: m.AutomationBuilderScreen })));
const DataSyncScreen = lazy(() => loadAutomationScreens().then((m) => ({ default: m.DataSyncScreen })));
const IntegrationsScreen = lazy(() => loadAutomationScreens().then((m) => ({ default: m.IntegrationsScreen })));
const loadPortalScreens = () => import("./screens/portals/PortalScreens");
const ClientPortalScreen = lazy(() => loadPortalScreens().then((m) => ({ default: m.ClientPortalScreen })));
const CmeWalletScreen = lazy(() => loadPortalScreens().then((m) => ({ default: m.CmeWalletScreen })));
const ParticipantPortalScreen = lazy(() => loadPortalScreens().then((m) => ({ default: m.ParticipantPortalScreen })));
const PortalApprovalsScreen = lazy(() => loadPortalScreens().then((m) => ({ default: m.PortalApprovalsScreen })));
const PortalCreativeScreen = lazy(() => loadPortalScreens().then((m) => ({ default: m.PortalCreativeScreen })));
const PortalDocumentsScreen = lazy(() => loadPortalScreens().then((m) => ({ default: m.PortalDocumentsScreen })));
const PortalFinanceScreen = lazy(() => loadPortalScreens().then((m) => ({ default: m.PortalFinanceScreen })));
const PortalWorkScreen = lazy(() => loadPortalScreens().then((m) => ({ default: m.PortalWorkScreen })));
const SpeakerPortalScreen = lazy(() => loadPortalScreens().then((m) => ({ default: m.SpeakerPortalScreen })));
const SponsorPortalScreen = lazy(() => loadPortalScreens().then((m) => ({ default: m.SponsorPortalScreen })));
const loadRegistrationScreens = () => import("./screens/registration/RegistrationScreens");
const AttendeeCommandScreen = lazy(() => loadRegistrationScreens().then((m) => ({ default: m.AttendeeCommandScreen })));
const RegistrationDetailScreen = lazy(() => loadRegistrationScreens().then((m) => ({ default: m.RegistrationDetailScreen })));
const RegistrationImportScreen = lazy(() => loadRegistrationScreens().then((m) => ({ default: m.RegistrationImportScreen })));
const RegistrationSetupScreen = lazy(() => loadRegistrationScreens().then((m) => ({ default: m.RegistrationSetupScreen })));
const RuleSimulatorScreen = lazy(() => loadRegistrationScreens().then((m) => ({ default: m.RuleSimulatorScreen })));
const loadScientificScreens = () => import("./screens/scientific/ScientificScreens");
const AbstractDecisionScreen = lazy(() => loadScientificScreens().then((m) => ({ default: m.AbstractDecisionScreen })));
const AbstractDetailScreen = lazy(() => loadScientificScreens().then((m) => ({ default: m.AbstractDetailScreen })));
const AbstractsScreen = lazy(() => loadScientificScreens().then((m) => ({ default: m.AbstractsScreen })));
const CommitteesScreen = lazy(() => loadScientificScreens().then((m) => ({ default: m.CommitteesScreen })));
const PresentationsScreen = lazy(() => loadScientificScreens().then((m) => ({ default: m.PresentationsScreen })));
const ProgramBuilderScreen = lazy(() => loadScientificScreens().then((m) => ({ default: m.ProgramBuilderScreen })));
const ProgramImportScreen = lazy(() => loadScientificScreens().then((m) => ({ default: m.ProgramImportScreen })));
const ProgramVersionsScreen = lazy(() => loadScientificScreens().then((m) => ({ default: m.ProgramVersionsScreen })));
const ReviewerWorkspaceScreen = lazy(() => loadScientificScreens().then((m) => ({ default: m.ReviewerWorkspaceScreen })));
const ScientificCommandScreen = lazy(() => loadScientificScreens().then((m) => ({ default: m.ScientificCommandScreen })));
const SpeakerProfileScreen = lazy(() => loadScientificScreens().then((m) => ({ default: m.SpeakerProfileScreen })));
const SpeakersScreen = lazy(() => loadScientificScreens().then((m) => ({ default: m.SpeakersScreen })));
const loadTravelScreens = () => import("./screens/travel/TravelScreens");
const FacultyTravelProfileScreen = lazy(() => loadTravelScreens().then((m) => ({ default: m.FacultyTravelProfileScreen })));
const HotelRoomingScreen = lazy(() => loadTravelScreens().then((m) => ({ default: m.HotelRoomingScreen })));
const TransportationControlScreen = lazy(() => loadTravelScreens().then((m) => ({ default: m.TransportationControlScreen })));
const TravelCommandScreen = lazy(() => loadTravelScreens().then((m) => ({ default: m.TravelCommandScreen })));
const loadWebsiteScreens = () => import("./screens/website/WebsiteScreens");
const PageSectionEditorScreen = lazy(() => loadWebsiteScreens().then((m) => ({ default: m.PageSectionEditorScreen })));
const WebsiteConceptScreen = lazy(() => loadWebsiteScreens().then((m) => ({ default: m.WebsiteConceptScreen })));
const WebsitePublishScreen = lazy(() => loadWebsiteScreens().then((m) => ({ default: m.WebsitePublishScreen })));
const WebsiteStudioScreen = lazy(() => loadWebsiteScreens().then((m) => ({ default: m.WebsiteStudioScreen })));
const PublicSiteScreen = lazy(() => loadWebsiteScreens().then((m) => ({ default: m.PublicSiteScreen })));
const loadWorkItemScreen = () => import("./screens/work/WorkItemScreen");
const WorkItemScreen = lazy(() => loadWorkItemScreen().then((m) => ({ default: m.WorkItemScreen })));




















/**
 * The route table.
 *
 * Every internal route is wrapped in the permission it needs. The API checks the
 * same key again on every call — this layer exists so a user who follows a link
 * they cannot use gets a clear answer instead of a screen that fails piece by
 * piece as its requests come back refused.
 */
export function App() {
  const { status, user } = useSession();

  if (status === "loading") return <FullPageWait />;

  const home = user?.isExternal ? "/portal" : "/work";

  return (
    <Suspense fallback={<FullPageWait />}>
      <Routes>
      <Route path="/login" element={<LoginScreen />} />
      {/* A published event website, readable without signing in. */}
      <Route path="/site/:slug" element={<PublicSiteScreen />} />

      <Route
        element={
          <RequireAuth>
            <Shell />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to={home} replace />} />

        {/* ---------------------------------------------------- Command */}
        <Route
          path="/work"
          element={
            <RequireInternal>
              <RequirePermission anyOf={[P.WorkView]}>
                <MyWorkScreen />
              </RequirePermission>
            </RequireInternal>
          }
        />
        <Route
          path="/work/:id"
          element={
            <RequirePermission anyOf={[P.WorkView]}>
              <WorkItemScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/executive"
          element={
            <RequirePermission anyOf={[P.ExecutiveView]}>
              <ExecutiveCommandScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/overview"
          element={
            <RequirePermission anyOf={[P.CommandView]}>
              <GlobalOverviewScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/alerts"
          element={
            <RequirePermission anyOf={[P.WorkView]}>
              <AlertsScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/approvals"
          element={
            <RequirePermission anyOf={[P.WorkView]}>
              <ApprovalsScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/risks"
          element={
            <RequirePermission anyOf={[P.WorkView]}>
              <RiskRegisterScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/incidents"
          element={
            <RequirePermission anyOf={[P.WorkView]}>
              <IncidentCommandScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/live"
          element={
            <RequirePermission anyOf={[P.OnsiteView]}>
              <LiveOperationsScreen />
            </RequirePermission>
          }
        />

        {/* ----------------------------------------------------- Events */}
        <Route
          path="/events"
          element={
            <RequirePermission anyOf={[P.EventsView]}>
              <EventsPortfolioScreen />
            </RequirePermission>
          }
        />
        {/*
          The event workspace. The address names the event, and the module screens inside it are the ones
          the sidebar opens, reading their event from the workspace instead of the switcher.
        */}
        <Route
          path="/events/:eventId"
          element={
            <RequirePermission anyOf={[P.EventsView]}>
              <EventWorkspace />
            </RequirePermission>
          }
        >
          <Route index element={<EventCommandCenterScreen />} />
          <Route
            path="settings"
            element={
              <RequirePermission anyOf={[P.EventsEdit]}>
                <EventSettingsScreen />
              </RequirePermission>
            }
          />
          <Route
            path="scientific"
            element={
              <RequirePermission anyOf={[P.ScientificView]}>
                <ScientificCommandScreen />
              </RequirePermission>
            }
          />
          <Route
            path="abstracts"
            element={
              <RequirePermission anyOf={[P.AbstractsView]}>
                <AbstractsScreen />
              </RequirePermission>
            }
          />
          <Route
            path="registration"
            element={
              <RequirePermission anyOf={[P.RegistrationView]}>
                <AttendeeCommandScreen />
              </RequirePermission>
            }
          />
          <Route
            path="sponsors"
            element={
              <RequirePermission anyOf={[P.SponsorsView]}>
                <SponsorCommandScreen />
              </RequirePermission>
            }
          />
          <Route
            path="exhibitors"
            element={
              <RequirePermission anyOf={[P.ExhibitorsView, P.SponsorsView]}>
                <ExhibitorMarketplaceScreen />
              </RequirePermission>
            }
          />
          <Route
            path="travel"
            element={
              <RequirePermission anyOf={[P.TravelView]}>
                <TravelCommandScreen />
              </RequirePermission>
            }
          />
          <Route
            path="creative"
            element={
              <RequirePermission anyOf={[P.CreativeView]}>
                <CreativeStudioScreen />
              </RequirePermission>
            }
          />
          <Route
            path="production"
            element={
              <RequirePermission anyOf={[P.ProductionView]}>
                <ProductionCommandScreen />
              </RequirePermission>
            }
          />
          <Route
            path="vendors"
            element={
              <RequirePermission anyOf={[P.VendorsView]}>
                <VendorRfqScreen />
              </RequirePermission>
            }
          />
          <Route
            path="finance"
            element={
              <RequirePermission anyOf={[P.FinanceView]}>
                <FinanceCommandScreen />
              </RequirePermission>
            }
          />
          <Route
            path="communications"
            element={
              <RequirePermission anyOf={[P.CommunicationsView]}>
                <CommunicationsCommandScreen />
              </RequirePermission>
            }
          />
          <Route
            path="website"
            element={
              <RequirePermission anyOf={[P.WebsiteView]}>
                <WebsiteStudioScreen />
              </RequirePermission>
            }
          />
          <Route
            path="onsite"
            element={
              <RequirePermission anyOf={[P.OnsiteView]}>
                <OnsiteCommandScreen />
              </RequirePermission>
            }
          />
          {/* The staff page reads the on-site dashboard, so it needs on-site access, as at /staff. */}
          <Route
            path="staff"
            element={
              <RequirePermission anyOf={[P.OnsiteView]}>
                <StaffDeploymentScreen />
              </RequirePermission>
            }
          />
          {/*
            The pages beneath each module, so a link followed from a module screen stays in the workspace
            (components/layout/workspacePath.ts). Each needs what it needs at its own address.
          */}
          <Route
            path="scientific/program"
            element={
              <RequirePermission anyOf={[P.ScientificView]}>
                <ProgramBuilderScreen />
              </RequirePermission>
            }
          />
          <Route
            path="scientific/program/import"
            element={
              <RequirePermission anyOf={[P.ScientificEdit]}>
                <ProgramImportScreen />
              </RequirePermission>
            }
          />
          <Route
            path="scientific/program/versions"
            element={
              <RequirePermission anyOf={[P.ScientificView]}>
                <ProgramVersionsScreen />
              </RequirePermission>
            }
          />
          <Route
            path="scientific/committees"
            element={
              <RequirePermission anyOf={[P.ScientificView]}>
                <CommitteesScreen />
              </RequirePermission>
            }
          />
          <Route
            path="scientific/speakers"
            element={
              <RequirePermission anyOf={[P.ScientificView]}>
                <SpeakersScreen />
              </RequirePermission>
            }
          />
          <Route
            path="scientific/speakers/:id"
            element={
              <RequirePermission anyOf={[P.ScientificView]}>
                <SpeakerProfileScreen />
              </RequirePermission>
            }
          />
          <Route
            path="scientific/presentations"
            element={
              <RequirePermission anyOf={[P.ScientificView]}>
                <PresentationsScreen />
              </RequirePermission>
            }
          />
          <Route
            path="abstracts/review"
            element={
              <RequirePermission anyOf={[P.AbstractsReview]}>
                <ReviewerWorkspaceScreen />
              </RequirePermission>
            }
          />
          <Route
            path="abstracts/decisions"
            element={
              <RequirePermission anyOf={[P.AbstractsDecide]}>
                <AbstractDecisionScreen />
              </RequirePermission>
            }
          />
          <Route
            path="abstracts/:id"
            element={
              <RequirePermission anyOf={[P.AbstractsView]}>
                <AbstractDetailScreen />
              </RequirePermission>
            }
          />
          <Route
            path="registration/setup"
            element={
              <RequirePermission anyOf={[P.RegistrationConfigure]}>
                <RegistrationSetupScreen />
              </RequirePermission>
            }
          />
          <Route
            path="registration/rules"
            element={
              <RequirePermission anyOf={[P.RegistrationConfigure]}>
                <RuleSimulatorScreen />
              </RequirePermission>
            }
          />
          <Route
            path="registration/import"
            element={
              <RequirePermission anyOf={[P.RegistrationCreate, P.IntegrationsView]}>
                <RegistrationImportScreen />
              </RequirePermission>
            }
          />
          <Route
            path="registration/:id"
            element={
              <RequirePermission anyOf={[P.RegistrationView]}>
                <RegistrationDetailScreen />
              </RequirePermission>
            }
          />
          <Route
            path="sponsors/builder"
            element={
              <RequirePermission anyOf={[P.SponsorsCreate, P.SponsorsEdit]}>
                <SponsorshipBuilderScreen />
              </RequirePermission>
            }
          />
          <Route
            path="sponsors/marketplace"
            element={
              <RequirePermission anyOf={[P.ExhibitorsView, P.SponsorsView]}>
                <ExhibitorMarketplaceScreen />
              </RequirePermission>
            }
          />
          <Route
            path="sponsors/:id"
            element={
              <RequirePermission anyOf={[P.SponsorsView]}>
                <SponsorDetailScreen />
              </RequirePermission>
            }
          />
          <Route
            path="travel/rooming"
            element={
              <RequirePermission anyOf={[P.TravelView]}>
                <HotelRoomingScreen />
              </RequirePermission>
            }
          />
          <Route
            path="travel/transport"
            element={
              <RequirePermission anyOf={[P.TravelView]}>
                <TransportationControlScreen />
              </RequirePermission>
            }
          />
          <Route
            path="travel/:profileId"
            element={
              <RequirePermission anyOf={[P.TravelView]}>
                <FacultyTravelProfileScreen />
              </RequirePermission>
            }
          />
          <Route
            path="creative/:id"
            element={
              <RequirePermission anyOf={[P.CreativeView]}>
                <CreativeDetailScreen />
              </RequirePermission>
            }
          />
          <Route
            path="production/:id"
            element={
              <RequirePermission anyOf={[P.ProductionView]}>
                <ProductionItemScreen />
              </RequirePermission>
            }
          />
          <Route
            path="finance/budget"
            element={
              <RequirePermission anyOf={[P.FinanceView]}>
                <EventBudgetScreen />
              </RequirePermission>
            }
          />
          <Route
            path="finance/invoices/:id"
            element={
              <RequirePermission anyOf={[P.FinanceView]}>
                <InvoiceDetailScreen />
              </RequirePermission>
            }
          />
          <Route
            path="communications/email"
            element={
              <RequirePermission anyOf={[P.CommunicationsView]}>
                <EmailStudioScreen />
              </RequirePermission>
            }
          />
          <Route
            path="communications/audiences"
            element={
              <RequirePermission anyOf={[P.CommunicationsView]}>
                <AudienceBuilderScreen />
              </RequirePermission>
            }
          />
          <Route
            path="communications/whatsapp"
            element={
              <RequirePermission anyOf={[P.CommunicationsView]}>
                <WhatsAppStudioScreen />
              </RequirePermission>
            }
          />
          <Route
            path="communications/senders"
            element={
              <RequirePermission anyOf={[P.CommunicationsConfigure]}>
                <SenderProfilesScreen />
              </RequirePermission>
            }
          />
          <Route
            path="communications/campaigns/:id"
            element={
              <RequirePermission anyOf={[P.CommunicationsView]}>
                <CampaignBuilderScreen />
              </RequirePermission>
            }
          />
          <Route
            path="website/concept"
            element={
              <RequirePermission anyOf={[P.WebsiteCreate, P.WebsiteEdit]}>
                <WebsiteConceptScreen />
              </RequirePermission>
            }
          />
          <Route
            path="website/publish"
            element={
              <RequirePermission anyOf={[P.WebsiteView]}>
                <WebsitePublishScreen />
              </RequirePermission>
            }
          />
          <Route
            path="website/pages/:pageId"
            element={
              <RequirePermission anyOf={[P.WebsiteEdit]}>
                <PageSectionEditorScreen />
              </RequirePermission>
            }
          />
          {/* The desk records check-ins, which needs onsite.create (OnsiteService.CheckInAsync). */}
          <Route
            path="onsite/checkin"
            element={
              <RequirePermission anyOf={[P.OnsiteCreate]}>
                <CheckInScreen />
              </RequirePermission>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Route>
        <Route
          path="/venues"
          element={
            <RequirePermission anyOf={[P.EventsView]}>
              <VenuesScreen />
            </RequirePermission>
          }
        />

        {/* -------------------------------------------------------- CRM */}
        <Route
          path="/pipeline"
          element={
            <RequirePermission anyOf={[P.CrmView]}>
              <SalesPipelineScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/pipeline/:id"
          element={
            <RequirePermission anyOf={[P.CrmView]}>
              <OpportunityDetailScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/clients"
          element={
            <RequirePermission anyOf={[P.ClientsView]}>
              <ClientsScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/clients/:id"
          element={
            <RequirePermission anyOf={[P.ClientsView]}>
              <ClientProfileScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/people"
          element={
            <RequirePermission anyOf={[P.PeopleView]}>
              <PeopleScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/people/:id"
          element={
            <RequirePermission anyOf={[P.PeopleView]}>
              <PersonProfileScreen />
            </RequirePermission>
          }
        />

        {/* --------------------------------------------------- Delivery */}
        <Route
          path="/projects"
          element={
            <RequirePermission anyOf={[P.ProjectsView]}>
              <ProjectsScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/projects/:id"
          element={
            <RequirePermission anyOf={[P.ProjectsView]}>
              <ProjectWorkspaceScreen />
            </RequirePermission>
          }
        />
        {/* The staff page reads the on-site dashboard, so it needs on-site access, not project access. */}
        <Route
          path="/staff"
          element={
            <RequirePermission anyOf={[P.OnsiteView]}>
              <StaffDeploymentScreen />
            </RequirePermission>
          }
        />

        {/* ------------------------------------------------- Scientific */}
        <Route
          path="/scientific"
          element={
            <RequirePermission anyOf={[P.ScientificView]}>
              <ScientificCommandScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/scientific/program"
          element={
            <RequirePermission anyOf={[P.ScientificView]}>
              <ProgramBuilderScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/scientific/program/import"
          element={
            <RequirePermission anyOf={[P.ScientificEdit]}>
              <ProgramImportScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/scientific/program/versions"
          element={
            <RequirePermission anyOf={[P.ScientificView]}>
              <ProgramVersionsScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/scientific/committees"
          element={
            <RequirePermission anyOf={[P.ScientificView]}>
              <CommitteesScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/scientific/speakers"
          element={
            <RequirePermission anyOf={[P.ScientificView]}>
              <SpeakersScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/scientific/speakers/:id"
          element={
            <RequirePermission anyOf={[P.ScientificView]}>
              <SpeakerProfileScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/scientific/presentations"
          element={
            <RequirePermission anyOf={[P.ScientificView]}>
              <PresentationsScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/abstracts"
          element={
            <RequirePermission anyOf={[P.AbstractsView]}>
              <AbstractsScreen />
            </RequirePermission>
          }
        />
        {/* External reviewers score abstracts here, so these two admit portal users. */}
        <Route
          path="/abstracts/review"
          element={
            <RequirePermission anyOf={[P.AbstractsReview]} allowExternal>
              <ReviewerWorkspaceScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/abstracts/decisions"
          element={
            <RequirePermission anyOf={[P.AbstractsDecide]}>
              <AbstractDecisionScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/abstracts/:id"
          element={
            <RequirePermission anyOf={[P.AbstractsView]} allowExternal>
              <AbstractDetailScreen />
            </RequirePermission>
          }
        />

        {/* ----------------------------------------------- Registration */}
        <Route
          path="/registration"
          element={
            <RequirePermission anyOf={[P.RegistrationView]}>
              <AttendeeCommandScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/registration/setup"
          element={
            <RequirePermission anyOf={[P.RegistrationConfigure]}>
              <RegistrationSetupScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/registration/rules"
          element={
            <RequirePermission anyOf={[P.RegistrationConfigure]}>
              <RuleSimulatorScreen />
            </RequirePermission>
          }
        />
        {/* Running an import needs registration.create; integrations.view can follow the import history. */}
        <Route
          path="/registration/import"
          element={
            <RequirePermission anyOf={[P.RegistrationCreate, P.IntegrationsView]}>
              <RegistrationImportScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/registration/:id"
          element={
            <RequirePermission anyOf={[P.RegistrationView]}>
              <RegistrationDetailScreen />
            </RequirePermission>
          }
        />

        {/* --------------------------------------------------- Sponsors */}
        <Route
          path="/sponsors"
          element={
            <RequirePermission anyOf={[P.SponsorsView]}>
              <SponsorCommandScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/sponsors/builder"
          element={
            <RequirePermission anyOf={[P.SponsorsCreate, P.SponsorsEdit]}>
              <SponsorshipBuilderScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/sponsors/marketplace"
          element={
            <RequirePermission anyOf={[P.ExhibitorsView, P.SponsorsView]}>
              <ExhibitorMarketplaceScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/sponsors/:id"
          element={
            <RequirePermission anyOf={[P.SponsorsView]}>
              <SponsorDetailScreen />
            </RequirePermission>
          }
        />

        {/* ----------------------------------------------------- Travel */}
        <Route
          path="/travel"
          element={
            <RequirePermission anyOf={[P.TravelView]}>
              <TravelCommandScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/travel/rooming"
          element={
            <RequirePermission anyOf={[P.TravelView]}>
              <HotelRoomingScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/travel/transport"
          element={
            <RequirePermission anyOf={[P.TravelView]}>
              <TransportationControlScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/travel/:profileId"
          element={
            <RequirePermission anyOf={[P.TravelView]}>
              <FacultyTravelProfileScreen />
            </RequirePermission>
          }
        />

        {/* ------------------------------------ Creative & production */}
        <Route
          path="/creative"
          element={
            <RequirePermission anyOf={[P.CreativeView]}>
              <CreativeStudioScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/creative/:id"
          element={
            <RequirePermission anyOf={[P.CreativeView]}>
              <CreativeDetailScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/production"
          element={
            <RequirePermission anyOf={[P.ProductionView]}>
              <ProductionCommandScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/production/:id"
          element={
            <RequirePermission anyOf={[P.ProductionView]}>
              <ProductionItemScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/procurement"
          element={
            <RequirePermission anyOf={[P.VendorsView]}>
              <VendorRfqScreen />
            </RequirePermission>
          }
        />

        {/* ---------------------------------------------------- Finance */}
        <Route
          path="/finance"
          element={
            <RequirePermission anyOf={[P.FinanceView]}>
              <FinanceCommandScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/finance/budget"
          element={
            <RequirePermission anyOf={[P.FinanceView]}>
              <EventBudgetScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/finance/invoices/:id"
          element={
            <RequirePermission anyOf={[P.FinanceView]}>
              <InvoiceDetailScreen />
            </RequirePermission>
          }
        />
        {/* Rates are the organisation's, not one event's; the API asks for finance.manage. */}
        <Route
          path="/finance/exchange-rates"
          element={
            <RequirePermission anyOf={[P.FinanceMargins]}>
              <ExchangeRatesScreen />
            </RequirePermission>
          }
        />

        {/* --------------------------------------------- Communications */}
        <Route
          path="/communications"
          element={
            <RequirePermission anyOf={[P.CommunicationsView]}>
              <CommunicationsCommandScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/communications/email"
          element={
            <RequirePermission anyOf={[P.CommunicationsView]}>
              <EmailStudioScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/communications/audiences"
          element={
            <RequirePermission anyOf={[P.CommunicationsView]}>
              <AudienceBuilderScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/communications/whatsapp"
          element={
            <RequirePermission anyOf={[P.CommunicationsView]}>
              <WhatsAppStudioScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/communications/senders"
          element={
            <RequirePermission anyOf={[P.CommunicationsConfigure]}>
              <SenderProfilesScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/communications/campaigns/:id"
          element={
            <RequirePermission anyOf={[P.CommunicationsView]}>
              <CampaignBuilderScreen />
            </RequirePermission>
          }
        />

        {/* ---------------------------------------------------- Website */}
        <Route
          path="/website"
          element={
            <RequirePermission anyOf={[P.WebsiteView]}>
              <WebsiteStudioScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/website/concept"
          element={
            <RequirePermission anyOf={[P.WebsiteCreate, P.WebsiteEdit]}>
              <WebsiteConceptScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/website/publish"
          element={
            <RequirePermission anyOf={[P.WebsiteView]}>
              <WebsitePublishScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/website/pages/:pageId"
          element={
            <RequirePermission anyOf={[P.WebsiteEdit]}>
              <PageSectionEditorScreen />
            </RequirePermission>
          }
        />

        {/* ----------------------------------------------------- Onsite */}
        <Route
          path="/onsite"
          element={
            <RequirePermission anyOf={[P.OnsiteView]}>
              <OnsiteCommandScreen />
            </RequirePermission>
          }
        />
        {/* The desk records check-ins, which needs onsite.create (OnsiteService.CheckInAsync). */}
        <Route
          path="/onsite/checkin"
          element={
            <RequirePermission anyOf={[P.OnsiteCreate]}>
              <CheckInScreen />
            </RequirePermission>
          }
        />

        {/* -------------------------------------------------- Documents */}
        <Route
          path="/documents"
          element={
            <RequirePermission anyOf={[P.DocumentsView]}>
              <DocumentsScreen />
            </RequirePermission>
          }
        />

        {/* ------------------------------------------------------- Intel */}
        <Route
          path="/ai"
          element={
            <RequirePermission anyOf={[P.AiView]}>
              <AiCommandCenterScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/analytics"
          element={
            <RequirePermission anyOf={[P.AnalyticsView]}>
              <AnalyticsCommandScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/analytics/reports"
          element={
            <RequirePermission anyOf={[P.AnalyticsView]}>
              <ReportBuilderScreen />
            </RequirePermission>
          }
        />

        {/* ---------------------------------------------------- Platform */}
        <Route
          path="/automations"
          element={
            <RequirePermission anyOf={[P.AutomationsView]}>
              <AutomationBuilderScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/integrations"
          element={
            <RequirePermission anyOf={[P.IntegrationsView]}>
              <IntegrationsScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/integrations/sync"
          element={
            <RequirePermission anyOf={[P.IntegrationsView]}>
              <DataSyncScreen />
            </RequirePermission>
          }
        />

        {/* ---------------------------------------------- Administration */}
        <Route
          path="/administration"
          element={
            <RequirePermission anyOf={[P.AdminView, P.AdminUsers]}>
              <UsersAccessScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/administration/groups"
          element={
            <RequirePermission anyOf={[P.AdminView, P.AdminUsers]}>
              <GroupsScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/administration/roles"
          element={
            <RequirePermission anyOf={[P.AdminView, P.AdminUsers]}>
              <RolesMatrixScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/administration/security"
          element={
            <RequirePermission anyOf={[P.AdminView]}>
              <SecurityAuditScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/administration/branding"
          element={
            <RequirePermission anyOf={[P.AdminConfigure]}>
              <TenantBrandingScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/administration/modules"
          element={
            <RequirePermission anyOf={[P.AdminConfigure]}>
              <FeatureFlagsScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/saas"
          element={
            <RequirePermission anyOf={[P.SaasView]}>
              <SaasTenantScreen />
            </RequirePermission>
          }
        />
        <Route
          path="/saas/plans"
          element={
            <RequirePermission anyOf={[P.SaasView]}>
              <SubscriptionPlansScreen />
            </RequirePermission>
          }
        />

        <Route path="/account" element={<AccountScreen />} />

        {/* ------------------------------------------------------ Portal */}
        <Route path="/portal" element={<ClientPortalScreen />} />
        <Route path="/portal/approvals" element={<PortalApprovalsScreen />} />
        <Route path="/portal/creative" element={<PortalCreativeScreen />} />
        <Route path="/portal/work" element={<PortalWorkScreen />} />
        <Route path="/portal/documents" element={<PortalDocumentsScreen />} />
        <Route path="/portal/finance" element={<PortalFinanceScreen />} />
        <Route path="/portal/speaker" element={<SpeakerPortalScreen />} />
        <Route path="/portal/sponsor" element={<SponsorPortalScreen />} />
        <Route path="/portal/me" element={<ParticipantPortalScreen />} />
        <Route path="/portal/cme" element={<CmeWalletScreen />} />

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
    </Suspense>
  );
}
